import React from 'react';
import useWebSocket from 'react-use-websocket';
import config from '../../../../../../config';
import useIsAppEnabled from '../../../../ApplicationSyncContext/useIsAppEnabled';
import useSessionInfo from '../../../Hooks/useSessionInfo';
import useSessionLanguage from '../../../Hooks/useSessionLanguage';
import useUsableIdToken from '../../../Hooks/useUsableIdToken';
import { AudioWebhookResponse } from '../../SessionConnectionContextProvider/useSessionConnectionContext';
import { AudioWebsocketApiConnectionContext } from './useAudioWebsocketApiConnectionContext';
import useDrainAwareReconnect from './useDrainAwareReconnect';

export default function AudioWebsocketApiConnectionContextProvider({ children }: { children: React.ReactNode }) {
  // Deliberately not useAmplifyContext().idToken: an expired token must not be handed to
  // the handshake at all, because the resulting 401 is invisible to the browser and the
  // socket would retry it forever. useUsableIdToken withholds it and asks for a refresh.
  const { idToken, isUsable: isIdTokenUsable, requestRefresh } = useUsableIdToken();
  const session = useSessionInfo();
  const sessionLanguage = useSessionLanguage();
  const [audioWebsocketConnectionEnabled, setAudioWebsocketConnectionEnabled] = React.useState<boolean>(false);
  const isAppEnabled = useIsAppEnabled();

  const sessionIdRef = React.useRef<string | null>(session?.id ?? null);

  React.useEffect(() => { sessionIdRef.current = session?.id ?? null; }, [session?.id]);

  const [connected, setConnected] = React.useState<boolean>(audioWebsocketConnectionEnabled);
  React.useEffect(() => {
    setConnected(audioWebsocketConnectionEnabled);
  }, [audioWebsocketConnectionEnabled]);

  // How many handshakes this connection cycle has taken, sent to the server as
  // numAttempts so audio_websocket_connection_attempts can report fleet-wide percentiles
  // of how many tries a session needed to get in. Counted here, in the one place that
  // runs exactly once per handshake (react-use-websocket re-resolves a function url on
  // every attempt), and reset by onOpen below once a handshake succeeds.
  //
  // Deliberately not derived from reconnectInterval's argument: that counter is the
  // socket's lifetime reconnect count and the library zeroes it on open, so a
  // long-established session that drops and reconnects on its first try would report 2
  // rather than 1, inflating every post-drop reconnect.
  //
  // A ref rather than state - a state update here would itself trigger a reconnect.
  const attemptNumberRef = React.useRef(0);

  // A draining pod completes the handshake and then closes with 1012, rather than failing
  // the handshake, so that the reason reaches the browser at all. Two counters reset
  // themselves as a result — react-use-websocket's backoff and attemptNumberRef, both
  // cleared by onOpen below — which is the intended outcome: being turned away by a pod
  // that is leaving should cost the caller nothing. What that leaves is the opposite
  // hazard, a retry loop with nothing to escalate it, which this handles.
  const drainAwareReconnect = useDrainAwareReconnect();

  const buildAudioWebsocketUrl = React.useCallback(() => {
    attemptNumberRef.current += 1;
    let url = `${config.Live.AudioWebsocketUrl}/session/${session?.id}?&asrLanguage=${sessionLanguage}&projectId=${session?.projectId}&numAttempts=${attemptNumberRef.current}`;
    if (isAppEnabled) {
      url += '&dualChannel=true';
    }
    return url;
  }, [session?.id, session?.projectId, sessionLanguage, isAppEnabled]);

  const audioWebsocketUrl = session?.id && session?.projectId ? buildAudioWebsocketUrl : null;

  const {
    sendMessage: sendRawMessage,
    sendJsonMessage, lastJsonMessage, readyState,
  } = useWebSocket<AudioWebhookResponse>(audioWebsocketUrl, {
    reconnectAttempts: Infinity,
    // A closed socket is worth retrying, but never with a token we already know the
    // server will reject: withholding it here pauses the loop, and the connect flag below
    // resumes it once a refreshed token arrives.
    shouldReconnect: () => isIdTokenUsable,
    onOpen: () => {
      // This cycle is over: the next handshake starts counting from 1 again.
      attemptNumberRef.current = 0;
      drainAwareReconnect.onOpen();
    },
    onClose: drainAwareReconnect.onClose,
    onError: (event: Event) => {
      console.error('Audio websocket connection error', event);
      // The browser gives us no status for a failed handshake, so we cannot tell 401 from
      // 429 from a network drop here. Asking for a refresh is cheap and idempotent, and it
      // covers the one cause the client can actually fix.
      requestRefresh();
    },
    protocols: ['cogneed-ws-protocol', ...idToken ? [idToken] : []],
    reconnectInterval: drainAwareReconnect.reconnectInterval,
  }, connected && !!session?.id && isIdTokenUsable);

  const sendMessage = React.useCallback((message: Uint8Array, index: number) => {
    const prefixBuffer = new ArrayBuffer(8);
    const prefixArray = new Uint8Array(prefixBuffer);
    prefixArray[7] = index;
    const fullArray = new Uint8Array(prefixArray.byteLength + message.byteLength);
    fullArray.set(prefixArray, 0);
    fullArray.set(new Uint8Array(message), prefixArray.byteLength);
    sendRawMessage(fullArray);
  }, [sendRawMessage]);

  React.useEffect(() => {
    let isMounted = true;
    if (session) {
      setAudioWebsocketConnectionEnabled(true);
    } else {
      setTimeout(() => {
        if (isMounted) {
          setAudioWebsocketConnectionEnabled(false);
        }
      }, 200);
    }
    return () => {
      isMounted = false;
    };
  }, [session]);

  const restart = React.useCallback(() => {
    setConnected(false);
    setTimeout(() => {
      setConnected(true);
    }, 200);
  }, []);

  const value = React.useMemo(() => ({
    sendJsonMessage,
    sendMessage,
    lastJsonMessage,
    readyState,
    restart,
  }), [sendJsonMessage, sendMessage, lastJsonMessage, readyState]);

  return (
    <AudioWebsocketApiConnectionContext.Provider value={value}>
      {children}
    </AudioWebsocketApiConnectionContext.Provider>
  );
}
